import React, { useState } from "react";
import {
    Box,
    Grid,
    Paper,
    Typography,
    Select,
    MenuItem,
    FormControl,
    FormHelperText,
    InputLabel
} from "@mui/material";
import { degreesRequest } from "../../../remote/degreesRequest";
import { occupationalFamilyTitlesRequest } from "../../../remote/occupationalFamilyTitlesRequest";
import { militaryCategoriesRequest } from "../../../remote/militaryCategoriesRequest";
import { industriesRequest } from "../../../remote/industriesRequest";
import { sectorsRequest } from "../../../remote/sectorsRequest"
import CustomTextField from "../../../components/FormComponents/CustomTextField"
import SelectSkills from "../../../components/FormComponents/SelectSkills";
import CustomDropdown from "../../../components/FormComponents/CustomDropdown";
import RadioSelect from "../../../components/FormComponents/RadioSelect";
import LoadingCircle from "../../../components/LayoutComponents/LoadingCircle";
import FormButtonLayout from "../../../components/LayoutComponents/FormButtonLayout"



export default function TemplateForm({ formData, setFormData, onSave, onDelete, session, competencies, proficiencies, templateTypes, skills, setSkills, loading }) {

    const [showSkillsPage, setShowSkillsPage] = useState(false);
    const [error, setError] = useState({});
    const handleChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };


    const handleSave = () => {
        const errors = {};

        if (!formData?.templateName || formData.templateName.trim() === "") {
            errors.templateName = "Template Name is required";
        }

        if (!formData?.templateDescription || formData.templateDescription.trim() === "") {
            errors.templateDescription = "Template Description is required";
        }

        if (!formData?.templateType || formData.templateType.trim() === "") {
            errors.templateType = "Template Type is required";
        }

        if (formData?.templateType === 'Work Experience' && !formData?.industryId) {
            errors.industryId = "Industry is required"
        }

        if (formData?.templateType === 'Military Experience' && !formData?.militaryCategoryId) {
            errors.militaryCategoryId = "Military Category is required";
        }

        if (Object.keys(errors).length > 0) {
            setError(errors);
            return;
        }

        setError({});
        onSave();
    };

    return (
        <>
            {showSkillsPage ? (
                <SelectSkills
                    competencies={competencies}
                    proficiencies={proficiencies}
                    selected={skills}
                    setSelected={setSkills}
                    setShowSkillsPage={setShowSkillsPage}
                    label={formData?.templateName || "Template "}
                    loading={loading}
                    error={Object.keys(error).length > 0}
                />
            ) :
                (<Paper sx={{ p: 3, display: "flex", flexDirection: "column", maxHeight: "60vh", overflowY: "auto" }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6">
                            Template Details
                        </Typography>

                        {loading && <LoadingCircle size={20} />}
                    </Box>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <CustomTextField
                                formData={formData}
                                field={'templateName'}
                                handleChange={handleChange}
                                required={true}
                                label="Template Name"
                                error={error?.templateName}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <CustomTextField
                                formData={formData}
                                field={'templateDescription'}
                                handleChange={handleChange}
                                required={true}
                                multiline={true}
                                rows={3}
                                label="Template Description"
                                error={error?.templateDescription}
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <RadioSelect value={formData?.activeFlag} label={formData.activeFlag ? "Active" : "Not Active"} onChange={(val) => handleChange("activeFlag", val)} />
                            <RadioSelect value={formData?.templateInternal} label={formData.templateInternal ? "Internal" : "External"} onChange={(val) => handleChange("templateInternal", val)} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth error={!!error.templateType}>
                                <InputLabel>Template Type</InputLabel>
                                <Select
                                    value={formData?.templateType || ""}
                                    onChange={(e) => handleChange("templateType", e.target.value)}
                                    label="Template Type"
                                    variant="standard"
                                >
                                    <MenuItem value="">Template Type</MenuItem>
                                    {templateTypes.map((type) => (
                                        <MenuItem key={type} value={type}>
                                            {type}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {error.templateType && (
                                    <FormHelperText>{error.templateType}</FormHelperText>
                                )}
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <CustomDropdown
                                session={session}
                                value={formData?.degreeTypeId}
                                setEntity={(degreeType) => handleChange("degreeTypeId", degreeType)}
                                style={{ width: "100%" }}
                                label="Degree Type"
                                disabled={formData.templateType === 'Education' || formData?.degreeTypeId ? false : true}
                                request={degreesRequest}
                                entityName="degreeTypeName"
                                entityIdField="degreeTypeID"
                                error={error.degreeTypeId}
                                helperText={error.degreeTypeId}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <CustomDropdown
                                session={session}
                                value={formData?.industryId}
                                setEntity={(industryId) => handleChange("industryId", industryId)}
                                setSector={(sectorId)=>handleChange("sectorId",sectorId)}
                                style={{ width: "100%" }}
                                label="Industry"
                                disabled={formData.templateType === 'Work Experience' ? false : true}
                                request={industriesRequest}
                                entityName="industryName"
                                entityIdField="industryId"
                                error={error.industryId}
                                helperText={error.industryId}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <CustomDropdown
                                session={session}
                                value={formData?.sectorId}
                                style={{ width: "100%" }}
                                label="Sector"
                                disabled={true}
                                request={sectorsRequest}
                                entityName="sectorName"
                                entityIdField="sectorId"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <CustomDropdown
                                session={session}
                                value={formData?.occupationFamilyTitlesId}
                                setEntity={(occupationFamilyTitlesId) => handleChange("occupationFamilyTitlesId", occupationFamilyTitlesId)}
                                style={{ width: "100%" }}
                                label="Occupational Family Titles"
                                disabled={formData.templateType === 'Work Experience' ? false : true}
                                request={occupationalFamilyTitlesRequest}
                                entityName="name"
                                entityIdField="id"
                                error={error.occupationFamilyTitlesId}
                                helperText={error.occupationFamilyTitlesId}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <CustomDropdown
                                session={session}
                                value={formData?.militaryCategoryId}
                                setEntity={(category) => handleChange("militaryCategoryId", category)}
                                style={{ width: "100%" }}
                                label="Military Category"
                                disabled={formData?.templateType === 'Military Experience' ? false : true}
                                request={militaryCategoriesRequest}
                                entityName="militaryCategoryName"
                                entityIdField="militaryCategoryId"
                                error={error.militaryCategoryId}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <CustomTextField
                                formData={formData}
                                field={'username'}
                                readOnly={true}
                                label="Owner"
                            />
                        </Grid>

                    </Grid>
                </Paper >
                )}

            <Grid item xs={12} sx={{ mt: 2 }}>
                <FormButtonLayout
                    onDelete={onDelete}
                    handleSave={handleSave}
                    showSkillsPage={showSkillsPage}
                    setShowSkillsPage={setShowSkillsPage}
                    selected={formData.templateId > 0} />
            </Grid>

        </>
    )
}

